import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { parseBrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";

import { getBrandProductsUseQueryArgs } from "../../preloadBrandPageLoggedIn";
import {
  IProductsData,
  productsDataAggregation,
} from "../utils/productsDataAggregator";

/**
 * @deprecated This function should not be directly used anymore.
 * We now populate this data server-side, so the context function
 * useBrandPageProductsStateContext should be used instead.
 */
export const useBrandProductsQueryWithQueryParams = (brandToken: string) => {
  const queryParams = useQueryParams(parseBrandPageQueryObj);
  return {
    productsQueryData: useBrandProductsQuery(brandToken, queryParams),
    brandToken,
    queryParams,
  };
};

// Only exported for testing.
export function useBrandProductsQuery(
  brandToken: string,
  queryParams: BrandPageQueryObj
): IProductsData {
  const {
    collectionsQueryKey,
    collectionsQueryFn,
    productsQueryKey,
    productsQueryFn,
    productFiltersQueryKey,
    productFiltersQueryFn,
    staleTime,
  } = useMemo(
    () => getBrandProductsUseQueryArgs(brandToken, queryParams),
    [brandToken, queryParams]
  );

  const { categories } = useCategoryStore();

  const collectionsQuery = useQuery({
    queryKey: collectionsQueryKey,
    queryFn: collectionsQueryFn,
    staleTime,
  });

  const productsQuery = useQuery({
    queryKey: productsQueryKey,
    queryFn: productsQueryFn,
    staleTime,
  });

  const productFiltersQuery = useQuery({
    queryKey: productFiltersQueryKey,
    queryFn: productFiltersQueryFn,
    placeholderData: keepPreviousData,
    staleTime,
  });

  return productsDataAggregation({
    queryParams,
    categories,
    collectionsData: collectionsQuery.data,
    collectionsQueryKey: collectionsQueryKey,
    collectionsQueryPending: collectionsQuery.isPending,
    collectionsQueryDataUpdatedAt: collectionsQuery.dataUpdatedAt,
    productsData: productsQuery.data,
    productsQueryKey: productsQueryKey,
    productsQueryPending: productsQuery.isPending,
    productsQueryDataUpdatedAt: productsQuery.dataUpdatedAt,
    filtersData: productFiltersQuery.data,
  });
}
